import { ProfileService } from './../../../core/services/profile/profile.service';
import { Component, OnInit } from '@angular/core';
import {
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { Profile, ProfileContact } from '../../models/profile.model';
import { MatIconModule } from '@angular/material/icon';
import { MainInfoComponent } from './main-info/main-info.component';
import { FormBuilder, Validators } from '@angular/forms';
import {
  DeliveryBillingForm,
  FormGroupTyped,
  MainInfoForm,
  PrimaryContactForm,
} from '../../../core/types/interfaces';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { PrimaryContactInfoComponent } from './primary-contact-info/primary-contact-info.component';
import { DeliveryBillingInfoComponent } from './delivery-billing-info/delivery-billing-info.component';
import { ButtonComponent } from '../button/button.component';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarService } from '../../../core/services/utils/snackbar.service';
import { ConfirmCustomerDataSuccessModalComponent } from './confirm-customer-data-success-modal/confirm-customer-data-success-modal.component';
import { CUSTOMER_DATA_CONFIRMED } from '../../../core/types/constants';

interface CustomerForm {
  mainInfo: MainInfoForm;
  primaryContact: PrimaryContactForm;
  delivery: DeliveryBillingForm;
  billing: DeliveryBillingForm;
}

type CustomerFormControls = {
  mainInfo: FormGroupTyped<MainInfoForm>;
  primaryContact: FormGroupTyped<PrimaryContactForm>;
  delivery: FormGroupTyped<DeliveryBillingForm>;
  billing: FormGroupTyped<DeliveryBillingForm>;
};

@Component({
  selector: 'app-confirm-customer-data-modal',
  templateUrl: './confirm-customer-data-modal.component.html',
  styleUrls: ['./confirm-customer-data-modal.component.scss'],
  imports: [
    MatDialogModule,
    MatIconModule,
    MainInfoComponent,
    PrimaryContactInfoComponent,
    DeliveryBillingInfoComponent,
    CommonModule,
    MatProgressSpinnerModule,
    ButtonComponent,
  ],
})
export class ConfirmCustomerDataModalComponent implements OnInit {
  profile: Profile;
  isLoading = false;
  isSaving = false;
  primaryContact: ProfileContact;
  customerForm: FormGroupTyped<CustomerForm, CustomerFormControls>;
  constructor(
    private profileService: ProfileService,
    private formBuilder: FormBuilder,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private dialogRef: MatDialogRef<ConfirmCustomerDataModalComponent>,
    private dialog: MatDialog
  ) {
    this.customerForm = this.formBuilder.group({
      mainInfo: this.formBuilder.group({
        email: ['', [Validators.required, Validators.email]],
        phone: [
          '',
          [Validators.required, Validators.pattern('^(?=.*[0-9])[-+ 0-9]+$')],
        ],
        taxId: [''],
      }),
      primaryContact: this.formBuilder.group({
        gender: ['', Validators.required],
        firstName: ['', Validators.required],
        lastName: ['', Validators.required],
        positionInFacility: ['', Validators.required],
        email: ['', Validators.required],
        phone: [
          Validators.required,
          Validators.pattern('^(?=.*[0-9])[-+ 0-9]+$'),
        ],
      }),
      delivery: this.formBuilder.group({
        customerName: ['', Validators.required],
        customerName2: [''],
        street: ['', Validators.required],
        houseNo: ['', Validators.required],
        postalCode: ['', Validators.required],
        city: ['', Validators.required],
      }),
      billing: this.formBuilder.group({
        customerName: ['', Validators.required],
        customerName2: [''],
        street: ['', Validators.required],
        houseNo: ['', Validators.required],
        postalCode: ['', Validators.required],
        city: ['', Validators.required],
      }),
    }) as FormGroupTyped<CustomerForm, CustomerFormControls>;
  }

  ngOnInit() {
    this.isLoading = true;
    this.profileService.getProfile().subscribe(profile => {
      this.profile = profile.data;
      this.primaryContact = this.profile.contacts.find(
        contact => contact.primaryContact
      );
      this.customerForm.patchValue({
        mainInfo: {
          email: this.profile.email,
          phone: this.profile.phone,
          taxId: this.profile.taxId,
        },
        primaryContact: {
          gender: this.primaryContact.gender,
          firstName: this.primaryContact.fname,
          lastName: this.primaryContact.lname,
          positionInFacility: this.primaryContact.jobRole,
          email: this.primaryContact.email,
          phone: this.primaryContact.phone,
        },
        delivery: {
          customerName: this.profile.deliveryAddress.nameLine1,
          customerName2: this.profile.deliveryAddress.nameLine2,
          street: this.profile.deliveryAddress.streetLine1,
          houseNo: this.profile.deliveryAddress.houseNo,
          postalCode: this.profile.deliveryAddress.zip,
          city: this.profile.deliveryAddress.city,
        },
        billing: {
          customerName: this.profile.billingAddress.nameLine1,
          customerName2: this.profile.billingAddress.nameLine2,
          street: this.profile.billingAddress.streetLine1,
          houseNo: this.profile.billingAddress.houseNo,
          postalCode: this.profile.billingAddress.zip,
          city: this.profile.billingAddress.city,
        },
      });

      if (this.profile.editBillingAddressInShop) {
        this.customerForm.get('billing').disable();
      }
      this.isLoading = false;
    });
  }

  private isFormInvalid(): boolean {
    if (this.customerForm.invalid) {
      this.customerForm.markAllAsTouched();
      return true;
    }
    return false;
  }
  private buildProfilePayload() {
    const formValue = this.customerForm.getRawValue();
    const contacts = this.profile.contacts.filter(
      contact => !contact.primaryContact
    );
    return new Profile({
      ...this.profile,
      id: this.profile.id,
      name: this.profile.name,
      responsiblePerson: this.profile.responsiblePerson || '',
      phone: formValue.mainInfo.phone,
      taxId: formValue.mainInfo.taxId,
      contacts: [
        ...contacts,
        {
          fname: formValue.primaryContact.firstName,
          lname: formValue.primaryContact.lastName,
          email: formValue.primaryContact.email,
          phone: formValue.primaryContact.phone,
          jobRole: formValue.primaryContact.positionInFacility,
          gender: formValue.primaryContact.gender,
          primary: true,
          primaryContact: true,
          id: this.primaryContact.id,
          position: this.primaryContact.position,
        } as any,
      ],
      notifications: this.profile.notifications,
      email: formValue.mainInfo.email,
      billingAddress: {
        ...this.profile.billingAddress,
        nameLine1: formValue.billing.customerName,
        nameLine2: formValue.billing.customerName2,
        streetLine1: formValue.billing.street,
        houseNo: formValue.billing.houseNo,
        zip: formValue.billing.postalCode,
        city: formValue.billing.city,
      },
      deliveryAddress: {
        ...this.profile.deliveryAddress,
        nameLine1: formValue.delivery.customerName,
        nameLine2: formValue.delivery.customerName2,
        streetLine1: formValue.delivery.street,
        houseNo: formValue.delivery.houseNo,
        zip: formValue.delivery.postalCode,
        city: formValue.delivery.city,
      },
    });
  }

  handleSuccessResponse(): void {
    this.dialogRef.close();
    localStorage.setItem(CUSTOMER_DATA_CONFIRMED, 'true');
    this.dialog.open(ConfirmCustomerDataSuccessModalComponent, {
      autoFocus: false,
      width: 'auto',
      height: 'auto',
    });
  }

  handleErrorResponse(): void {
    this.isSaving = false;
    this.snackbarService.error(
      this.translateService.instant('profile.error.inProfieConfirming')
    );
  }

  confirmCustomerData() {
    if (this.isFormInvalid()) {
      return;
    }
    this.isSaving = true;
    const profilePayload = this.buildProfilePayload();
    this.profileService.updateProfile(profilePayload).subscribe({
      next: () => this.handleSuccessResponse(),
      error: () => this.handleErrorResponse(),
    });
  }
}
